import { Controller } from "@hotwired/stimulus"

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let str = ""
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
  const pad = base64.length % 4 === 0 ? "" : "=".repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export default class extends Controller {
  static targets = ["status", "button"]

  async authenticate(event) {
    event.preventDefault()

    this.#setLoading(true)

    try {
      const csrfToken = document.querySelector("meta[name='csrf-token']").content

      const challengeResponse = await fetch("/passkeys/authentication_challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken }
      })
      const options = await challengeResponse.json()

      options.challenge = base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({
          ...c, id: base64urlToBuffer(c.id)
        }))
      }

      const credential = await navigator.credentials.get({ publicKey: options })

      const authResponse = await fetch("/passkeys/authenticate", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              authenticatorData: bufferToBase64url(credential.response.authenticatorData),
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
              signature: bufferToBase64url(credential.response.signature),
              userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
            }
          }
        })
      })

      const result = await authResponse.json()
      if (authResponse.ok) {
        this.#setStatus("Signing in...", "text-success mt-2")
        window.location.href = result.redirect_to || "/"
      } else {
        this.#setLoading(false)
        this.#setStatus(result.error || "Authentication failed.", "text-danger mt-2")
      }
    } catch (err) {
      this.#setLoading(false)
      if (err.name === "NotAllowedError") {
        this.#setStatus("Authentication was cancelled.", "text-warning mt-2")
      } else {
        this.#setStatus("Authentication failed: " + err.message, "text-danger mt-2")
      }
    }
  }

  #setLoading(loading) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = loading
    this.buttonTarget.innerHTML = loading
      ? `<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Signing in…`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-fingerprint me-2" viewBox="0 0 16 16"><path d="M8.06 6.5a.5.5 0 0 1 .5.5v5.5a.5.5 0 0 1-1 0V7a.5.5 0 0 1 .5-.5"/><path d="M5.56 1.074c.73-.248 1.59-.347 2.44-.347s1.71.1 2.44.347a7 7 0 0 1 2.275 1.326c.636.595 1.135 1.326 1.435 2.19.3.862.4 1.853.2 2.96a.5.5 0 0 1-.98-.196c.17-.935.09-1.76-.148-2.456-.24-.698-.637-1.3-1.168-1.795A5.96 5.96 0 0 0 10.1 1.83 6.5 6.5 0 0 0 8 1.527c-.783 0-1.47.085-2.1.302a5.96 5.96 0 0 0-1.945 1.073c-.53.495-.928 1.097-1.168 1.795-.237.697-.318 1.52-.148 2.456a.5.5 0 0 1-.98.196c-.2-1.107-.1-2.098.2-2.96.3-.864.8-1.595 1.435-2.19A7 7 0 0 1 5.56 1.074M8 4.2c-.745 0-1.296.144-1.706.38-.41.235-.695.552-.893.926A4.3 4.3 0 0 0 5 7v5.5a.5.5 0 0 1-1 0V7c0-.593.097-1.235.346-1.844.25-.61.65-1.17 1.243-1.58C6.23 3.168 7.012 2.95 8 2.95s1.77.218 2.41.626c.594.41.994.97 1.244 1.58.249.61.346 1.251.346 1.844v.5a.5.5 0 0 1-1 0V7c0-.514-.082-1.05-.401-1.494-.198-.374-.483-.69-.893-.926C9.296 4.344 8.745 4.2 8 4.2"/><path d="M6.06 7c0-.363.054-.7.182-1.006.129-.307.336-.583.656-.79C7.21 4.998 7.58 4.9 8 4.9s.79.098 1.102.304c.32.207.527.483.656.79.128.307.182.643.182 1.006v5.5a.5.5 0 0 1-1 0V7a1.8 1.8 0 0 0-.108-.576 1 1 0 0 0-.31-.37C8.384 5.96 8.212 5.9 8 5.9s-.384.06-.522.154a1 1 0 0 0-.31.37A1.8 1.8 0 0 0 7.06 7v5.5a.5.5 0 0 1-1 0z"/></svg>Sign in with Passkey`
    if (loading) this.#setStatus("", "")
  }

  #setStatus(message, className) {
    this.statusTarget.textContent = message
    this.statusTarget.className = className
  }
}
